
import React, { useState } from 'react';
import { SubscriptionPlan } from '@/types/subscriptionPlans';
import PlanPreviewCard from './PlanPreviewCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface PlansPreviewTabProps {
  plans: SubscriptionPlan[];
}

const PlansPreviewTab: React.FC<PlansPreviewTabProps> = ({ plans }) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  
  // Filter active plans and sort by price
  const activePlans = [...plans]
    .filter(plan => plan.name && plan.description)
    .sort((a, b) => {
      // Sort by price based on the selected billing period
      if (billingPeriod === 'monthly') {
        return a.monthlyPrice - b.monthlyPrice;
      } else if (billingPeriod === 'quarterly') {
        const aPrice = a.monthlyPrice * 3 * (1 - (a.quarterlyDiscount || 0) / 100);
        const bPrice = b.monthlyPrice * 3 * (1 - (b.quarterlyDiscount || 0) / 100);
        return aPrice - bPrice;
      } else {
        const aPrice = a.monthlyPrice * 12 * (1 - (a.yearlyDiscount || 0) / 100);
        const bPrice = b.monthlyPrice * 12 * (1 - (b.yearlyDiscount || 0) / 100);
        return aPrice - bPrice;
      }
    });

  return (
    <Card className="border-none shadow-md bg-gradient-to-br from-white to-blue-50">
      <CardHeader className="pb-2">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
          <div>
            <CardTitle className="text-xl text-blue-800">Subscription Plans Preview</CardTitle>
            <CardDescription>
              How your customers will see your subscription plans
            </CardDescription>
          </div>
          <div className="w-full md:w-auto">
            <Tabs
              value={billingPeriod}
              onValueChange={(value) => setBillingPeriod(value as 'monthly' | 'quarterly' | 'yearly')}
              className="w-full md:w-auto"
            >
              <TabsList className="grid w-full grid-cols-3 bg-blue-50 border border-blue-100">
                <TabsTrigger 
                  value="monthly"
                  className="data-[state=active]:bg-white data-[state=active]:text-blue-700"
                >
                  Monthly
                </TabsTrigger>
                <TabsTrigger 
                  value="quarterly"
                  className="data-[state=active]:bg-white data-[state=active]:text-blue-700"
                >
                  Quarterly
                </TabsTrigger>
                <TabsTrigger 
                  value="yearly"
                  className="data-[state=active]:bg-white data-[state=active]:text-blue-700"
                >
                  Yearly
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
          {activePlans.length === 0 ? (
            <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <p className="text-gray-500">No active plans to display. Please create and configure plans in the management tab.</p>
            </div>
          ) : (
            activePlans.map(plan => (
              <PlanPreviewCard
                key={plan.id}
                plan={plan}
              />
            ))
          )}
        </div>

        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <h3 className="text-lg font-medium mb-2 text-blue-800">How your customers will see these plans</h3>
          <p className="text-gray-600">
            This is a preview of how your subscription plans will appear to potential customers on your website. 
            Plans are sorted by price from lowest to highest. 
            Customers will be able to toggle between monthly, quarterly, and yearly billing options to see different pricing tiers.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlansPreviewTab;
